export class ElectricArc {
  static isFlickering: boolean = false;
  constructor(
    public x: number,
    public y: number,
    public sequenceNumber: number,
    public delayMs: number = 500
  ) {
    this.sequenceNumber = sequenceNumber;
    this.x = x;
    this.y = y;
    this.delayMs = delayMs;
  }

  /**
   * Draws a jagged lightning arc through the given ElectricArc points using Phaser 3 Graphics.
   * @param scene The Phaser.Scene to add the graphics to.
   * @param arcs Array of ElectricArc points (in order).
   * @param options Optional: { color, thickness, jaggedness, alpha }
   * @returns The Phaser.GameObjects.Graphics object for further control/animation.
   */
  static drawLightningArc(
    scene: Phaser.Scene,
    arcs: Array<ElectricArc>,
    options: { color?: number; thickness?: number; jaggedness?: number; alpha?: number } = {}
  ): Phaser.GameObjects.Graphics {
    const color = options.color ?? 0x00ffff;
    const thickness = options.thickness ?? 3;
    const jaggedness = options.jaggedness ?? 12;
    const alpha = options.alpha ?? 1;
    const graphics = scene.add.graphics();
    graphics.clear();
    graphics.lineStyle(thickness, color, alpha);
    const sortedArcs = arcs.sort((a, b) => a.sequenceNumber - b.sequenceNumber);
    for(let n=0; n<2; n++) {
      for (let i = 0; i < sortedArcs.length - 1; i++) {
        const start = sortedArcs[i];
        const end = sortedArcs[i + 1];
        // Draw a jagged line between start and end
        const points = ElectricArc.getJaggedLinePoints(start, end, jaggedness);
        graphics.beginPath();
        graphics.moveTo(points[0].x, points[0].y);
        for (let j = 1; j < points.length; j++) {
          graphics.lineTo(points[j].x, points[j].y);
        }
        graphics.strokePath();
      }
    }
    return graphics;
  }

  /**
   * Helper to generate jagged points between two ElectricArc points.
   */
  private static getJaggedLinePoints(start: ElectricArc, end: ElectricArc, jaggedness: number): { x: number; y: number }[] {
    const points = [];
    const segments = Phaser.Math.Between(6, 12);
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      // Linear interpolation
      let x = Phaser.Math.Interpolation.Linear([start.x, end.x], t);
      let y = Phaser.Math.Interpolation.Linear([start.y, end.y], t);
      // Add random jaggedness except for endpoints
      if (i !== 0 && i !== segments) {
        x += Phaser.Math.Between(-jaggedness, jaggedness);
        y += Phaser.Math.Between(-jaggedness, jaggedness);
      }
      points.push({ x, y });
    }
    return points;
  }

  /**
   * Animates the electric arc by redrawing it every few frames for a flicker effect.
   * @param scene The Phaser.Scene to add the graphics to.
   * @param arcs Array of ElectricArc points (in order).
   * @param durationMs How long the arc should last (ms)
   * @param options Optional: { color, thickness, jaggedness, alpha, flickerMs }
   */
  static animateElectricArc(
    scene: Phaser.Scene,
    arcs: Array<ElectricArc>,
    durationMs: number = 10000,
    options: { color?: number; thickness?: number; jaggedness?: number; alpha?: number; flickerMs?: number } = {}
  ) {
    if (ElectricArc.isFlickering) return;
    ElectricArc.isFlickering = true;
    const flickerMs = options.flickerMs ?? 50;
    let graphics: Phaser.GameObjects.Graphics | null = null;
    let elapsed = 0;
    const redraw = () => {
      if (graphics) graphics.destroy();
      graphics = ElectricArc.drawLightningArc(scene, arcs, options);
    };
    redraw();
    const timer = scene.time.addEvent({
      delay: flickerMs,
      repeat: Math.floor(durationMs / flickerMs),
      callback: () => {
        redraw();
        elapsed += flickerMs;
        if (elapsed >= durationMs) {
          if (graphics) graphics.destroy();
          ElectricArc.isFlickering = false;
        }
      },
    });
    return graphics;
  }
}